import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { Loading } from "components";
import { NotAuthenticated, ZonaUnavailable } from "features";

import { useQuery } from "utils/hooks/useQuery";
import { getFromLocalStorage, saveToLocalStorage } from "utils/localStorage";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  selectParentToken,
  selectTeacherEvaluationToken,
  setParentToken,
  setTeacherEvaluationToken,
  setToken,
} from "store/slices/token";

import { useLoginMutation } from "api/api";

import {
  ACCESS_TOKEN_PARAM,
  EXTERNAL_ID,
  HANDSHAKE_QUERY_PARAM,
  PARENT_TOKEN_PARAM,
  TEACHER_EVALUATION_TOKEN_PARAM,
  USER_GRADE,
} from "const";

import { processApiError } from "utils/processApiError";

import BgWaveTop from "images/bg/bckg-branded-top.svg";
import { getExternalId, getGrade } from "store/slices/user";

interface Props {
  children: ReactNode;
}

// Status `login` při výpadku Zóny (DNS, timeout, 5xx), ne její odpověď. [TD-496]
const ZONA_UNAVAILABLE = "zona_unavailable";
export const ZONA_RETRY_MS = 30_000;
export const ZONA_RETRY_LIMIT_MS = 30 * 60_000;

const Authenticator: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const token = getFromLocalStorage(ACCESS_TOKEN_PARAM);
  const parentToken = useAppSelector(selectParentToken);
  const teacherEvaluationToken = useAppSelector(selectTeacherEvaluationToken);
  const externalId = useAppSelector(getExternalId);
  const userGrade = useAppSelector(getGrade);

  const { getSearchParam, hideSearchParams } = useQuery();
  const [login, { error: apiMutationError }] = useLoginMutation();

  const [authDone, setAuthDone] = useState(false);
  const [zonaUnavailableSince, setZonaUnavailableSince] = useState<number | null>(null);
  const [zonaRetryExpired, setZonaRetryExpired] = useState(false);
  const userIsBeingLoggedIn = useRef(false);
  const queryAccessToken = getSearchParam(HANDSHAKE_QUERY_PARAM);
  const queryParentToken = getSearchParam(PARENT_TOKEN_PARAM);
  const queryTeacherEvaluationToken = getSearchParam(TEACHER_EVALUATION_TOKEN_PARAM);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processApiError(apiMutationError);
  }, [apiMutationError]);

  useEffect(() => {
    if (authDone) return;

    if (queryTeacherEvaluationToken !== null) {
      processTeacherEvaluation(queryTeacherEvaluationToken);
      setAuthDone(true);
      return;
    }

    if (import.meta.env.VITE_LOGIN_USER !== undefined) {
      processAdmin(import.meta.env.VITE_LOGIN_USER);
      return;
    }

    if (queryAccessToken !== null) {
      processUser(queryAccessToken);
      return;
    }

    if (queryParentToken !== null) {
      processParent(queryParentToken);
      setAuthDone(true);
      return;
    }

    const storageAccessToken = getFromLocalStorage<string>(ACCESS_TOKEN_PARAM);
    if (storageAccessToken !== null) {
      dispatch(setToken(storageAccessToken));
      setAuthDone(true);
      return;
    }

    const storageParentToken = getFromLocalStorage<string>(PARENT_TOKEN_PARAM);
    if (storageParentToken !== null) {
      dispatch(setParentToken(storageParentToken));
      setAuthDone(true);
      return;
    }

    const storageTeacherEvalutionToken = getFromLocalStorage<string>(TEACHER_EVALUATION_TOKEN_PARAM);
    if (storageParentToken !== null) {
      dispatch(setTeacherEvaluationToken(storageTeacherEvalutionToken));
      setAuthDone(true);
      return;
    }

    updateToken(null);
    updateParentToken(null);
    updateTeacherEvaluationToken(null);

    setAuthDone(true);
  }, [queryAccessToken, queryParentToken, queryTeacherEvaluationToken]);

  // AIDEV-NOTE: Handshake je jednorázový, ale backend si po výpadku Zóny podrží jeho výsledek
  // (`HandshakeRepository`), takže nový pokus se stejným handshakem projde i tehdy, když Zóna
  // vypadla až u osobních údajů. Opakuje se po ZONA_RETRY_MS nejdéle ZONA_RETRY_LIMIT_MS
  // (výpadek 11. 10. 2026 trval přes hodinu), pak zůstane jen tlačítko. [TD-496]
  useEffect(() => {
    if (zonaUnavailableSince === null || queryAccessToken === null) return;

    const interval = window.setInterval(() => {
      if (Date.now() - zonaUnavailableSince >= ZONA_RETRY_LIMIT_MS) {
        window.clearInterval(interval);
        setZonaRetryExpired(true);
        return;
      }

      processUser(queryAccessToken);
    }, ZONA_RETRY_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [zonaUnavailableSince]);

  useEffect(() => {
    saveToLocalStorage(EXTERNAL_ID, externalId);
    saveToLocalStorage(USER_GRADE, userGrade);
  }, [externalId]);

  const processAdmin = (token: string) => {
    setAuthDone(true);
    dispatch(setToken(token));
    saveToLocalStorage(ACCESS_TOKEN_PARAM, token);
  };

  const processUser = (token: string) => {
    updateParentToken(null);
    updateTeacherEvaluationToken(null);

    if (userIsBeingLoggedIn.current) return;
    userIsBeingLoggedIn.current = true;

    let zonaUnavailable = false;

    login({ token })
      .then(response => {
        if ("data" in response && typeof response.data?.login.accessToken === "string") {
          updateToken(response.data.login.accessToken);
        } else if ("data" in response && response.data?.login.status === ZONA_UNAVAILABLE) {
          zonaUnavailable = true;
          setZonaUnavailableSince(since => since ?? Date.now());
        } else {
          console.error(response);
          updateToken(null);
        }
      })
      .catch(error => {
        console.error(error);
        updateToken(null);
      })
      .finally(() => {
        userIsBeingLoggedIn.current = false;
        // Výpadek Zóny: token z dřívějška se nemaže a handshake zůstává v adrese pro nový pokus.
        if (zonaUnavailable) return;

        setZonaUnavailableSince(null);
        setAuthDone(true);
        hideSearchParams();
      });
  };

  const continueWithStoredToken = () => {
    dispatch(setToken(getFromLocalStorage<string>(ACCESS_TOKEN_PARAM)));
    setZonaUnavailableSince(null);
    setAuthDone(true);
    hideSearchParams();
  };

  const processParent = (token: string) => {
    updateToken(null);
    updateTeacherEvaluationToken(null);

    saveToLocalStorage(PARENT_TOKEN_PARAM, token ?? undefined);
    dispatch(setParentToken(token));

    setAuthDone(true);
    hideSearchParams();
  };

  const processTeacherEvaluation = (token: string) => {
    updateToken(null);
    updateParentToken(null);

    saveToLocalStorage(TEACHER_EVALUATION_TOKEN_PARAM, token ?? undefined);
    dispatch(setTeacherEvaluationToken(token));

    setAuthDone(true);
    hideSearchParams();
  };

  const updateToken = (token: string | null) => {
    saveToLocalStorage(ACCESS_TOKEN_PARAM, token ?? undefined);
    dispatch(setToken(token));

    if (token !== null) {
      updateParentToken(null);
      updateTeacherEvaluationToken(null);
    }
  };

  const updateParentToken = (token: string | null) => {
    dispatch(setParentToken(queryParentToken));

    if (token !== null) {
      updateToken(null);
      updateTeacherEvaluationToken(null);
    }
  };

  const updateTeacherEvaluationToken = (token: string | null) => {
    dispatch(setTeacherEvaluationToken(queryTeacherEvaluationToken));

    if (token !== null) {
      updateToken(null);
      updateParentToken(null);
    }
  };

  if (zonaUnavailableSince !== null && queryAccessToken !== null) {
    return (
      <ZonaUnavailable
        hasStoredToken={typeof token === "string" && token.length > 0}
        isRetrying={!zonaRetryExpired}
        onRetry={() => {
          processUser(queryAccessToken);
        }}
        onContinue={continueWithStoredToken}
      />
    );
  }

  if (!authDone) {
    return (
      <div className={"fullpage bg-primary-subtle"}>
        <div className={"wave__top"}>
          <BgWaveTop />
        </div>

        <Loading />
      </div>
    );
  }

  if (
    (typeof token === "string" && token.length > 0) ||
    (typeof parentToken === "string" && parentToken.length > 0) ||
    (typeof teacherEvaluationToken === "string" && teacherEvaluationToken.length > 0)
  ) {
    return <>{children}</>;
  }

  return <NotAuthenticated />;
};

export default Authenticator;
